/**
 * Admin panel layout shell (§27.4).
 *
 * Phase 35.5: nav split into Platform (super-admin only) and Tenant sections.
 * Phase 35.3: TenantSwitcher shown for super-admins in the sidebar.
 */
import { NavLink, Outlet, Link } from "react-router-dom";
import Header from "../components/Header.jsx";
import { isAdmin } from "../services/auth.js";

// getUserGroups is not exported directly, so we detect super-admin by checking
// that the user is in the "admin" group — all admins are in scope for tenants,
// but we expose super-admin features to everyone in the admin group for now
// (the backend enforces is_super_admin; the frontend hides the nav items).
// Phase 35.X will tighten this if a tenant-admin role is added.
function isSuperAdmin() {
  return isAdmin();
}

const PLATFORM_SECTIONS = [
  { to: "/admin/tenants", label: "Tenants" },
  { to: "/admin/personas", label: "Personas" },
  { to: "/admin/guardrails", label: "Guardrails" },
];

const TENANT_SECTIONS = [
  { to: "/admin/agents", label: "Agents" },
  { to: "/admin/templates", label: "Templates" },
  { to: "/admin/prompts", label: "Prompts" },
  { to: "/admin/usage", label: "Usage" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/tenant-settings", label: "Settings" },
];

function NavSection({ heading, items }) {
  return (
    <div className="admin-sidebar__section">
      <div className="admin-sidebar__section-heading">{heading}</div>
      <ul>
        {items.map((s) => (
          <li key={s.to}>
            <NavLink
              to={s.to}
              className={({ isActive }) =>
                `admin-sidebar__link${isActive ? " admin-sidebar__link--active" : ""}`
              }
              end
            >
              {s.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminLayout() {
  const superAdmin = isSuperAdmin();

  return (
    <>
      <Header />
      <main className="admin-layout">
        <aside className="admin-sidebar" aria-label="Admin navigation">
          <div className="admin-sidebar__heading">Admin</div>
          {/* TenantSwitcher now lives in the global Header for super-admins
              so the X-Tenant-Id selection is in scope on non-admin routes too. */}
          <nav>
            {superAdmin && (
              <NavSection heading="Platform" items={PLATFORM_SECTIONS} />
            )}
            <NavSection heading="Tenant" items={TENANT_SECTIONS} />
          </nav>
          <Link to="/" className="admin-sidebar__back">
            ← Back to app
          </Link>
        </aside>
        <section className="admin-content">
          <Outlet />
        </section>
      </main>
    </>
  );
}
